import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  AccountInfo,
  AuthenticationResult,
} from "@azure/msal-browser";
import { b2cConfig, loginScopes } from "../config/msalConfig";
import { signOut, setTokens, IdTokenClaims } from "../store/slices/authSlice";
import { IRootReducerState } from "../store/IRootReducer";
import { authService } from "../utils/authService";

/**
 * Custom hook that wraps @azure/msal-react hooks with application-specific logic
 *
 * This hook provides:
 * - Authentication state management
 * - Token acquisition (silent and interactive)
 * - Logout functionality
 * - User information access
 * - Authorization headers generation
 * - B2C-specific error handling (AADB2C90091, AADB2C90118)
 */
export function useMsalAuth() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();

  const accessToken = useSelector((state: IRootReducerState) => state.authReducer.accessToken);
  const idTokenClaims = useSelector((state: IRootReducerState) => state.authReducer.idTokenClaims);

  /**
   * Get the current active account
   */
  const getAccount = useCallback((): AccountInfo | null => {
    return accounts[0] || null;
  }, [accounts]);

  /**
   * Check if user is logged in
   */
  const isLoggedIn = useCallback((): boolean => {
    return isAuthenticated && accounts.length > 0;
  }, [isAuthenticated, accounts]);

  /**
   * Check if authentication interaction is in progress
   */
  const loginInProgress = useCallback((): boolean => {
    return (
      sessionStorage.getItem(`msal.${b2cConfig.clientId}.interaction.status`) ===
      "interaction_in_progress"
    );
  }, []);

  /**
   * Get access token (silent with fallback to interactive)
   *
   * @param scopes Optional scopes to request (defaults to config scopes)
   * @returns Access token string or null if interaction is in progress
   */
  const getAccessToken = useCallback(
    async (scopes?: string[]): Promise<string | null> => {
      try {
        const account = getAccount();
        if (!account) {
          console.warn("No account found, cannot acquire token");
          return null;
        }

        let tokenResponse: AuthenticationResult;

        try {
          // Try silent token acquisition first
          tokenResponse = await instance.acquireTokenSilent({
            account: account,
            scopes: scopes || b2cConfig.scopes,
          });
        } catch (error) {
          console.error("Silent token acquisition failed:", error);

          if (error instanceof InteractionRequiredAuthError) {
            // Check if interaction is already in progress
            if (loginInProgress()) {
              console.warn(
                "Authentication interaction already in progress, skipping acquireTokenRedirect",
              );
              return null;
            }

            // Fall back to interactive token acquisition
            await instance.acquireTokenRedirect({
              account: account,
              scopes: scopes || b2cConfig.scopes,
            });

            // Redirect will happen, return null
            return null;
          }

          throw error;
        }

        // Update Redux store with tokens
        if (tokenResponse) {
          dispatch(
            setTokens({
              accessToken: tokenResponse.accessToken,
              idTokenClaims: tokenResponse.idTokenClaims as IdTokenClaims,
            }),
          );
          return tokenResponse.accessToken;
        }

        return null;
      } catch (error) {
        console.error("Error acquiring access token:", error);
        return null;
      }
    },
    [instance, getAccount, loginInProgress, dispatch],
  );

  /**
   * Perform login redirect
   *
   * @param scopes Optional scopes to request
   */
  const login = useCallback(
    async (scopes?: string[]): Promise<void> => {
      if (loginInProgress()) {
        console.warn("Login already in progress");
        return;
      }

      try {
        await instance.loginRedirect({
          scopes: scopes || loginScopes,
        });
      } catch (error) {
        console.error("Login failed:", error);
        throw error;
      }
    },
    [instance, loginInProgress],
  );

  /**
   * Perform logout
   * Calls backend logout API, clears Redux state, and performs MSAL logout
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Call backend logout API using current token from Redux
      if (accessToken) {
        await fetch("/api/user/logout", {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`,
          },
        });
      }

      // Clear Redux state
      dispatch(signOut());

      // Clear tenant/instance selection BEFORE redirect (redirect prevents code after it from running)
      authService.clearInstanceSelection();

      // Perform MSAL logout (this redirects, so code after this won't execute)
      await instance.logoutRedirect({
        postLogoutRedirectUri: b2cConfig.postLogoutRedirectUri,
      });
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  }, [instance, accessToken, dispatch]);

  /**
   * Get current user information
   * Returns user data from idTokenClaims in Redux store
   */
  const currentUser = useCallback(() => {
    const account = getAccount();

    if (!account) {
      return null;
    }

    if (idTokenClaims) {
      const claims = idTokenClaims as IdTokenClaims & {
        given_name?: string;
        family_name?: string;
        city?: string;
        country?: string;
        jobtitle?: string;
        extension_IsPlatformAdmin?: boolean;
        account_Enabled?: boolean;
      };
      return {
        name: claims.name,
        firstName: claims.given_name,
        lastName: claims.family_name,
        emails: claims.email,
        city: claims.city,
        country: claims.country,
        jobtitle: claims.jobtitle,
        isPlatformAdmin: claims.extension_IsPlatformAdmin,
        isAccountEnabled: claims.account_Enabled,
        id: claims.sub,
      };
    }

    return {
      name: account.name,
      id: account.localAccountId,
    };
  }, [getAccount, idTokenClaims]);

  /**
   * Get authorization header for API calls
   * @returns Headers object with Authorization and InstanceId
   */
  const getAuthorizationHeader = useCallback((): Headers => {
    const instanceId = new URLSearchParams(window.location.search).get("instanceid");

    return new Headers({
      "Accept": "application/json",
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken || ""}`,
      "InstanceId": instanceId || "",
    });
  }, [accessToken]);

  /**
   * Get header as plain object (for axios and other libraries)
   */
  const getHeader = useCallback(() => {
    const instanceId = new URLSearchParams(window.location.search).get("instanceid");

    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken || ""}`,
      "InstanceId": instanceId || "",
    };
  }, [accessToken]);

  /**
   * Get multipart authorization header (without Content-Type)
   */
  const getMultiPartAuthorizationHeader = useCallback((): Headers => {
    const instanceId = new URLSearchParams(window.location.search).get("instanceid");

    return new Headers({
      Authorization: `Bearer ${accessToken || ""}`,
      InstanceId: instanceId || "",
    });
  }, [accessToken]);

  /**
   * Get instance ID from query string or localStorage
   */
  const getInstanceId = useCallback((): string => {
    return authService.getInstanceId();
  }, []);

  /**
   * Get query string with instance ID
   */
  const getQueryString = useCallback((): string => {
    const instanceId = getInstanceId();
    return instanceId ? `?instanceid=${instanceId}` : "";
  }, [getInstanceId]);

  return {
    // MSAL instance and state
    instance,
    accounts,
    isAuthenticated,
    isLoggedIn: isLoggedIn(),

    // Authentication methods
    login,
    logout,
    getAccessToken,

    // User information
    currentUser: currentUser(),
    getAccount,

    // Helper methods
    getAuthorizationHeader,
    getHeader,
    getMultiPartAuthorizationHeader,
    getInstanceId,
    getQueryString,
    loginInProgress: loginInProgress(),
  };
}
